import { fallbackSnapshot } from "./fixture";
import type { AnalysisStatus, CalibrationSummary, ChangeItem, Confidence, DashboardSnapshotV2, Direction, EvidenceItem, HistoryPoint, PlayerProfile, Recommendation } from "./types";

type Raw = Record<string, any>;

const isSnapshot = (value: unknown): value is DashboardSnapshotV2 => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<DashboardSnapshotV2>;
  return candidate.meta?.schemaVersion === "dashboard_snapshot.v2" && Boolean(candidate.today && candidate.myTeam && candidate.matchup);
};

const number = (value: unknown, fallback = 0) => typeof value === "number" && Number.isFinite(value) ? value : fallback;
const text = (value: unknown, fallback = "") => typeof value === "string" && value.trim() ? value : fallback;
const list = <T = Raw>(value: unknown): T[] => Array.isArray(value) ? value as T[] : [];
const percent = (value: unknown) => Math.round(number(value) * (number(value) <= 1 ? 100 : 1));
const optionalPercent = (value: unknown) => typeof value === "number" && Number.isFinite(value) ? Math.round(value * (value <= 1 ? 100 : 1) * 10) / 10 : null;
const confidence = (value: unknown): Confidence => {
  const label = text(value).toLowerCase();
  if (label === "high" || label === "medium" || label === "low") return label;
  return number(value) >= 75 ? "high" : number(value) >= 45 ? "medium" : "low";
};
const direction = (value: unknown): Direction => {
  const label = text(value).toLowerCase();
  if (label.includes("ris") || label.includes("increas") || label === "up" || label === "positive") return "up";
  if (label.includes("fall") || label.includes("decreas") || label === "down" || label === "negative") return "down";
  return "flat";
};
const analysisStatus = (value: unknown, fallback: AnalysisStatus = "partial"): AnalysisStatus => {
  const label = text(value).toLowerCase().replaceAll("_", "-");
  if (["ready", "complete", "verified", "fresh"].includes(label)) return "ready";
  if (["stale", "expired", "aging"].includes(label)) return "stale";
  if (["unavailable", "missing", "rejected", "blocked"].includes(label)) return "unavailable";
  if (["partial", "degraded", "limited"].includes(label)) return "partial";
  return fallback;
};
const pickupConfidence = (value: unknown): Confidence | "unknown" => {
  if (text(value).toLowerCase() === "unknown") return "unknown";
  return confidence(value);
};
const humanLabel = (value: string) => value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
const compactEvidenceValue = (value: unknown, depth = 0): string => {
  if (typeof value === "number") return Number.isInteger(value) ? String(value) : value.toFixed(2);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string") return value.trim() || "Not reported";
  if (Array.isArray(value)) {
    if (!value.length) return "None reported";
    const shown = value.slice(0, 3).map((item) => compactEvidenceValue(item, depth + 1));
    return `${shown.join("; ")}${value.length > shown.length ? `; +${value.length - shown.length} more` : ""}`;
  }
  if (value && typeof value === "object") {
    const row = value as Raw;
    const action = text(row.action);
    const subject = text(row.player || row.add || row.drop || row.team_name);
    if (action || subject) return `${action} ${subject}`.trim();
    const direct = text(row.headline || row.summary || row.message || row.name || row.label || row.status);
    if (direct) return direct;
    if (depth >= 2) return "Structured evidence";
    const entries = Object.entries(row).slice(0, 4);
    if (!entries.length) return "None reported";
    return entries.map(([key, item]) => `${humanLabel(key)}: ${compactEvidenceValue(item, depth + 1)}`).join(" · ");
  }
  return "Not reported";
};
const displayValue = (value: unknown, fallback = "Evidence available") => {
  if (value === undefined || value === null) return fallback;
  return compactEvidenceValue(value);
};

function evidenceRows(value: unknown, prefix: string): EvidenceItem[] {
  const rows: Array<Raw | string> = Array.isArray(value)
    ? value as Array<Raw | string>
    : value && typeof value === "object"
      ? Object.entries(value as Raw).map(([key, item]) => {
        const structured = item && typeof item === "object" && !Array.isArray(item) && ["id", "label", "metric", "name", "type", "value", "summary", "message", "detail"].some((field) => field in (item as Raw));
        return structured ? { key, ...(item as Raw) } : { key, label: humanLabel(key), value: item };
      })
      : [];
  return rows.map((row, index) => {
    if (typeof row === "string") return { id: `${prefix}-${index}`, label: "Evidence", value: row };
    return {
      id: text(row.id || row.fact_id || row.key, `${prefix}-${index}`),
      label: text(row.label || row.metric || row.name || row.type, "Evidence"),
      value: displayValue(row.value ?? row.summary ?? row.message ?? row.detail),
      detail: text(row.detail || row.context || row.why_it_matters) || undefined,
      source: text(row.source || row.source_name || row.publisher) || undefined,
      asOf: text(row.as_of || row.evidence_as_of || row.published_at) || undefined,
      confidence: row.confidence === undefined ? undefined : confidence(row.confidence),
    };
  });
}

function changeRows(value: unknown, prefix: string): ChangeItem[] {
  return list<Raw | string>(value).map((row, index) => {
    if (typeof row === "string") return { id: `${prefix}-${index}`, title: row, detail: "Material change in the accepted snapshot.", direction: "flat", happenedAt: "Current refresh" };
    return {
      id: text(row.id || row.change_id, `${prefix}-${index}`),
      title: text(row.title || row.headline || row.label || row.field, "Accepted analysis changed"),
      detail: text(row.detail || row.summary || row.message || row.reason, "See the current accepted evidence."),
      direction: direction(row.direction || row.trend || row.change),
      happenedAt: text(row.happened_at || row.changed_at || row.current_as_of || row.as_of, "Current refresh"),
      previous: row.previous === undefined ? undefined : displayValue(row.previous),
      current: row.current === undefined ? undefined : displayValue(row.current),
      delta: typeof row.delta === "number" ? row.delta : null,
      materiality: text(row.materiality) || undefined,
    };
  });
}

function historyRows(value: unknown): HistoryPoint[] {
  const rawValue = value && typeof value === "object" && !Array.isArray(value) ? value as Raw : {};
  const rows = Array.isArray(value) ? list<Raw>(value) : list<Raw>(rawValue.points);
  return rows.map((row) => ({
    asOf: text(row.as_of || row.generated_at || row.date),
    score: typeof row.score === "number" ? row.score : undefined,
    seasonValue: typeof row.season_value === "number" ? row.season_value : typeof row.ros_value === "number" ? row.ros_value : undefined,
    weeklyImpact: typeof row.weekly_impact === "number" ? row.weekly_impact : undefined,
    note: text(row.note || row.summary || row.change) || undefined,
    metrics: row.metrics && typeof row.metrics === "object"
      ? Object.fromEntries(Object.entries(row.metrics as Raw).filter(([, metric]) => metric === null || typeof metric === "number")) as Record<string, number | null>
      : undefined,
  })).filter((row) => Boolean(row.asOf));
}

function calibrationSummary(value: unknown): CalibrationSummary | undefined {
  if (!value || typeof value !== "object") return undefined;
  const row = value as Raw;
  return {
    status: analysisStatus(row.analysis_status?.state || row.analysis_status || row.status, "partial"),
    summary: text(row.summary || row.message, "Calibration evidence is available for the current model."),
    sampleSize: typeof row.sample_size === "number" ? row.sample_size : typeof row.n === "number" ? row.n : null,
    coverage: typeof row.coverage === "number" ? percent(row.coverage) : null,
    observedAccuracy: typeof row.observed_accuracy === "number" ? percent(row.observed_accuracy) : typeof row.accuracy === "number" ? percent(row.accuracy) : null,
    predictionsCollected: typeof row.predictions_collected === "number" ? row.predictions_collected : undefined,
    outcomesCollected: typeof row.outcomes_collected === "number" ? row.outcomes_collected : undefined,
    scoredPredictions: typeof row.scored_predictions === "number" ? row.scored_predictions : undefined,
    brierScore: typeof row.brier_score === "number" ? row.brier_score : null,
    noLookaheadGate: typeof row.no_lookahead_gate === "boolean" ? row.no_lookahead_gate : undefined,
    asOf: text(row.as_of || row.evaluated_at) || undefined,
  };
}

function recommendation(row: Raw): Recommendation {
  const cut = text(row.cut_status).toLowerCase();
  const suggestion = text(row.suggestion).toLowerCase();
  if (cut === "recommended drop" || cut === "cut") return "cut";
  if (cut === "review") return "watch";
  if (text(row.status) === "rostered") return "hold";
  if (suggestion.includes("stream")) return "stream";
  if (suggestion.includes("watch") || suggestion.includes("monitor")) return "watch";
  return "add";
}

function playerMapper(profileMap: Map<string, Raw>) {
  return (row: Raw): PlayerProfile => {
    const profile = profileMap.get(text(row.player_key)) ?? {};
    const weekly = number(row.weekly_impact);
    const starts = list<Raw>(row.starts);
    const threeStartOutlook = text(row.three_start_outlook);
    const rec = recommendation(row);
    const cutStatus = text(row.cut_status).toLowerCase();
    return {
      id: text(row.player_key, `player-${text(row.name).toLowerCase().replace(/\W+/g, "-")}`),
      name: text(row.name, "Unknown player"),
      team: text(row.team, "FA"),
      positions: text(row.positions, "—").split(/\s*,\s*/).filter(Boolean),
      age: typeof profile.age === "number" ? profile.age : null,
      rosterStatus: text(row.roster_slot || row.status),
      availability: text(row.availability || row.screening_status || row.status),
      recommendation: rec,
      score: number(row.overall_score, number(row.ros_value, 50)),
      trend: direction(row.trend || profile.trend),
      trendLabel: `${text(row.trend || profile.trend, "Steady")} trajectory`,
      seasonValue: number(row.ros_value, 50),
      weeklyImpact: Math.max(0, Math.min(100, (weekly + 1) * 50)),
      confidence: confidence(row.confidence || profile.confidence),
      essence: text(row.essence || profile.essence, `${text(row.name, "This player")} is awaiting a complete profile.`),
      outlook: threeStartOutlook
        ? `${threeStartOutlook} ${text(profile.outlook)}`.trim()
        : text(profile.outlook, "Use the accepted decision score and role evidence as the current outlook."),
      suggestion: text(row.suggestion || profile.suggestion, "Wait for stronger evidence."),
      risk: text(row.risk || profile.risk, "No specific modeled risk is available."),
      trigger: text(profile.trigger, starts[0] ? `Recheck before the ${text(starts[0].date)} probable start.` : "Recheck after the next accepted role or lineup update."),
      categories: [],
      sourceAsOf: text(profile.evidence_as_of),
      isMine: text(row.status) === "rostered",
      externalGuidance: row.external_guidance ? { tier: text(row.external_guidance.tier), publishedAt: text(row.external_guidance.published_at), sourceLink: /^https:\/\/(?:www\.)?pitcherlist\.com\//.test(text(row.external_guidance.source_link)) ? text(row.external_guidance.source_link) : "", expectedStarts: list<Raw>(row.external_guidance.expected_starts).map((start) => ({ date: text(start.date), opponent: text(start.opponent), away: Boolean(start.away) })), historicalRank: typeof row.external_guidance.historical_rank === "number" ? row.external_guidance.historical_rank : null, historicalAsOf: text(row.external_guidance.historical_as_of) } : undefined,
      protectedCore: row.protected_core === true || row.core_protected === true || ["protect", "protected"].includes(text(row.cut_status).toLowerCase()),
      nextStarts: starts.slice(0, 3).map((start) => ({ date: text(start.date), opponent: text(start.opponent, "Opponent pending"), probability: optionalPercent(start.start_probability), status: text(start.status || start.certainty, "Projected") })),
      cutRisk: rec === "cut" ? 82 : cutStatus === "watch" ? 45 : 10,
      probableStarts: starts.length,
      inningsOutlook: starts.length ? Number(starts.reduce((sum, start) => sum + number(start.expected_ip) * number(start.start_probability, 1), 0).toFixed(1)) : undefined,
      strikeoutOutlook: starts.length ? Number(starts.reduce((sum, start) => sum + number(start.expected_k) * number(start.start_probability, 1), 0).toFixed(1)) : undefined,
      ratioRisk: typeof row.matchup_risk === "number" ? percent(row.matchup_risk) : undefined,
      analysisStatus: analysisStatus(row.analysis_status || profile.analysis_status, "partial"),
      keyEvidence: evidenceRows(row.key_evidence ?? profile.key_evidence, `player-evidence-${text(row.player_key)}`),
      whatChanged: changeRows(row.what_changed ?? profile.what_changed, `player-change-${text(row.player_key)}`),
      history: historyRows(row.history ?? profile.history),
    };
  };
}

function normalizeRawSnapshot(raw: Raw): DashboardSnapshotV2 {
  const views = raw.views ?? {};
  const profiles = list<Raw>(raw.player_profiles);
  const profileMap = new Map(profiles.map((row) => [text(row.player_key), row]));
  const mapPlayer = playerMapper(profileMap);
  const teamView = views.my_team ?? {};
  const leagueView = views.league ?? {};
  const matchupView = views.matchup ?? {};
  const todayView = views.today ?? {};
  const hitterRows = list<Raw>(views.hitters?.table).map(mapPlayer);
  const pitcherRows = list<Raw>(views.pitchers?.table).map(mapPlayer);
  const roster = list<Raw>(teamView.roster).map(mapPlayer);
  const acceptedCutLineValues = list<Raw>(teamView.cut_line).map((row) => number(row.ros_value, Number.NaN)).filter(Number.isFinite);
  const rawStandings = list<Raw>(leagueView.standings);
  const myStanding = rawStandings.find((row) => row.is_my_team) ?? {};
  const rivalStanding = rawStandings.find((row) => row.is_featured_rival) ?? {};
  const powerByTeam = new Map(list<Raw>(leagueView.power_rankings).map((row) => [text(row.team_name), number(row.power_score, 50)]));
  const overall = optionalPercent(matchupView.summary?.win_probability);
  const rawCategories = list<Raw>(matchupView.categories);
  const baseCategoryOdds = new Map(rawCategories.map((row) => [text(row.category), percent(row.win_probability)]));
  const categoryWins = (rows: Raw[]) => rows.filter((row) => number(row.category_equity, number(row.equity)) >= .5).length;
  const rawScenarios = list<Raw>(matchupView.scenarios);
  const scenarios = [
    { id: "current", label: "Current roster", description: "Keep the accepted roster and category plan unchanged.", winProbability: overall, categoryWins: categoryWins(rawCategories), expectedCategoryDelta: 0, risk: "Low", deltas: {} },
    ...rawScenarios.map((scenario, index) => {
      const scenarioCategories = list<Raw>(scenario.categories);
      return {
        id: `scenario-${index + 1}`,
        label: text(scenario.scenario, `Scenario ${index + 1}`),
        description: `Model-tested roster scenario: ${text(scenario.scenario)}.`,
        winProbability: optionalPercent(scenario.win_probability),
        categoryWins: categoryWins(scenarioCategories),
        expectedCategoryDelta: number(scenario.expected_category_delta),
        risk: number(scenario.ip_minimum_probability_delta) < 0 ? "Higher" : "Modeled",
        deltas: Object.fromEntries(scenarioCategories.map((row) => [text(row.category), percent(row.win_probability) - (baseCategoryOdds.get(text(row.category)) ?? 0)])),
      };
    }),
  ];
  const storyRows = list<Raw>(todayView.stories);
  const firstStory = storyRows[0] ?? {};
  const secondStory = list<Raw>(todayView.stories)[1] ?? firstStory;
  const methodSources = list<Raw>(raw.sources);
  const metadata = raw.metadata ?? {};
  const asOf = text(metadata.as_of);
  const freshnessDomains = raw.freshness?.domains ?? {};
  const sourceFreshness = Object.entries(freshnessDomains).slice(0, 6).map(([name, value]) => ({ label: name.replaceAll("_", " "), asOf: text((value as Raw).as_of, asOf), status: raw.freshness?.state === "stale" ? "stale" as const : "fresh" as const }));
  const actionRows = list<Raw>(todayView.actions);
  const watchRows = list<Raw>(todayView.watch_list);
  const transactionRows = list<Raw>(views.transactions?.table);
  const worthwhileIds = new Set(list<Raw>(views.transactions?.worthwhile_drops).map((row) => text(row.player_key)));
  const rawAnalysis = raw.analysis_status && typeof raw.analysis_status === "object" ? raw.analysis_status as Raw : {};
  const rawHistory = raw.history && typeof raw.history === "object" ? raw.history as Raw : {};
  const normalizedStories = storyRows.map((story, index) => {
    const materiality = story.materiality && typeof story.materiality === "object" ? story.materiality as Raw : {};
    const change = story.change && typeof story.change === "object" ? story.change as Raw : {};
    return {
      id: text(story.id || story.story_id, `story-${index}`),
      section: text(story.section, "Today"),
      headline: text(story.headline, "Accepted analysis"),
      summary: text(story.summary),
      narrative: text(story.narrative, text(story.summary)),
      whyItMatters: text(story.why_it_matters),
      actionTrigger: text(story.action_trigger),
      confidence: confidence(story.confidence),
      sources: evidenceRows(story.sources, `story-source-${index}`),
      materiality: Object.keys(materiality).length ? {
        score: typeof materiality.score === "number" ? materiality.score : null,
        tier: text(materiality.tier, "Unrated"),
        decisionImpact: typeof materiality.decision_impact === "number"
          ? `${percent(materiality.decision_impact)}% modeled decision impact`
          : text(materiality.decision_impact, "Decision impact is not separately rated."),
        newness: typeof materiality.newness === "number"
          ? `${percent(materiality.newness)}% new evidence`
          : text(materiality.newness, "Current accepted snapshot"),
      } : undefined,
      change: Object.keys(change).length ? {
        status: text(change.status, "unchanged"),
        summary: text(change.summary, "No material change was reported."),
        previousAsOf: text(change.previous_as_of) || undefined,
      } : undefined,
    };
  });
  const storySources = normalizedStories.flatMap((story) => story.sources);
  const mostMaterialStory = [...normalizedStories].filter((story) => story.materiality).sort((a, b) => (b.materiality?.score ?? -1) - (a.materiality?.score ?? -1))[0];
  const rawNarrative = todayView.narrative ?? firstStory.narrative;
  const narrativeRow = rawNarrative && typeof rawNarrative === "object" ? rawNarrative as Raw : {};
  const narrativeParagraphs = Array.isArray(narrativeRow.paragraphs)
    ? list<string>(narrativeRow.paragraphs).map((row) => text(row)).filter(Boolean)
    : typeof rawNarrative === "string" ? [rawNarrative] : [];
  const rawMateriality = todayView.materiality && typeof todayView.materiality === "object"
    ? todayView.materiality as Raw
    : mostMaterialStory?.materiality
      ? { is_material: mostMaterialStory.materiality.tier.toLowerCase() !== "none", level: mostMaterialStory.materiality.tier, reason: mostMaterialStory.materiality.decisionImpact }
      : {};
  const materialityLabel = text(rawMateriality.level || rawMateriality.tier || rawMateriality.status, rawMateriality.is_material ? "medium" : "none").toLowerCase();
  const materialityLevel = (materialityLabel.includes("high") || materialityLabel.includes("critical")
    ? "high"
    : materialityLabel.includes("medium") || materialityLabel.includes("material")
      ? "medium"
      : materialityLabel.includes("low") || materialityLabel.includes("routine")
        ? "low"
        : "none") as "high" | "medium" | "low" | "none";

  return {
    meta: {
      schemaVersion: `dashboard_snapshot.v${number(raw.schema_version, 2)}`,
      snapshotId: text(metadata.source_snapshot, "accepted-snapshot"),
      generatedAt: text(metadata.generated_at, asOf),
      leagueName: "Fantasy league",
      teamName: text(teamView.team_name, text(myStanding.team_name, "My team")),
      opponentName: text(metadata.current_matchup?.opponent_name || matchupView.opponent_name || leagueView.playoff_context?.opponent?.team_name, "Opponent not verified"),
      featuredRivalName: text(rivalStanding.team_name, "Double Baggers"),
      evidenceAsOf: asOf,
      rosterEffectiveDate: typeof metadata.roster_effective_date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(metadata.roster_effective_date) && !Number.isNaN(Date.parse(metadata.roster_effective_date)) && new Date(metadata.roster_effective_date).toISOString().slice(0, 10) === metadata.roster_effective_date ? metadata.roster_effective_date : undefined,
      weekLabel: `As of ${new Date(asOf).toLocaleDateString([], { month: "short", day: "numeric" })}`,
      dataQuality: metadata.status === "fresh" ? "verified" : metadata.status === "stale" ? "stale" : "partial",
      freshness: sourceFreshness.length ? sourceFreshness : methodSources.map((source) => ({ label: text(source.name), asOf: text(source.as_of, asOf), status: "fresh" as const })),
      analysisStatus: analysisStatus(rawAnalysis.state || metadata.analysis_status || metadata.status, "partial"),
      keyEvidence: evidenceRows(todayView.key_evidence ?? metadata.key_evidence ?? raw.key_evidence, "snapshot-evidence"),
      whatChanged: changeRows(todayView.what_changed ?? rawHistory.changes ?? metadata.what_changed ?? raw.what_changed, "snapshot-change"),
      history: historyRows(rawHistory),
      analysisUnknowns: [...list<string>(rawAnalysis.unknowns), ...list<string>(matchupView.summary?.evidence_limits)].map((row) => text(row)).filter(Boolean),
      evidenceGates: evidenceRows(rawAnalysis.evidence_gates, "evidence-gate"),
      analysisAcceptedOnly: typeof rawAnalysis.accepted_only === "boolean" ? rawAnalysis.accepted_only : undefined,
      historyAcceptedOnly: typeof rawHistory.accepted_only === "boolean" ? rawHistory.accepted_only : undefined,
    },
    today: {
      headline: text(firstStory.headline, "Today’s accepted game plan"),
      deck: text(firstStory.summary, "The current snapshot is ready for a quick decision review."),
      brief: list<Raw>(todayView.brief).map((row) => text(row.message)).filter(Boolean),
      actions: actionRows.map((row, index) => ({
        id: `action-${index + 1}`,
        priority: index + 1,
        label: index === 0 ? "Best move" : text(row.action, "Next call"),
        player: text(row.player || row.add || row.drop, "Roster"),
        decision: row.add || row.drop ? `${text(row.action)} ${text(row.add)}${row.drop ? ` · Drop ${text(row.drop)}` : ""}` : `${text(row.action, "Hold")} ${text(row.player)}`.trim(),
        why: text(row.status, text(secondStory.why_it_matters, "Clears the accepted evidence gates.")),
        impact: `${number(row.expected_category_delta) >= 0 ? "+" : ""}${number(row.expected_category_delta).toFixed(2)} expected category wins`,
        deadline: text(row.trigger, "Recheck after the next accepted update."),
        expiresAt: text(row.expires_at || row.deadline_at) || undefined,
        confidence: confidence(row.confidence),
        factIds: [],
      })),
      changes: changeRows(todayView.what_changed ?? rawHistory.changes, "today-change").length
        ? changeRows(todayView.what_changed ?? rawHistory.changes, "today-change")
        : list<Raw>(todayView.stories).slice(0, 3).map((row, index) => ({ id: `story-${index}`, title: text(row.headline), detail: text(row.why_it_matters || row.summary), direction: "flat" as Direction, happenedAt: "Current accepted report" })),
      watchList: watchRows.map((row, index) => ({
        id: `category-${index}`,
        name: text(row.name), team: "Matchup category", positions: [], age: null, recommendation: "watch" as Recommendation,
        score: percent(row.win_probability), trend: "flat" as Direction, trendLabel: "Current model", seasonValue: percent(row.win_probability), weeklyImpact: percent(row.win_probability), confidence: "medium" as Confidence,
        essence: text(row.message), outlook: "This category is close enough to monitor.", suggestion: "Follow the matchup strategy.", risk: "Small sample and remaining games can move the odds.", trigger: `Recheck when ${text(row.name)} changes materially.`, categories: [text(row.name)], sourceAsOf: asOf,
      })),
      keyEvidence: evidenceRows(todayView.key_evidence, "today-evidence"),
      narrative: narrativeParagraphs.length || narrativeRow.lede ? {
        lede: text(narrativeRow.lede || narrativeRow.headline || narrativeRow.summary, narrativeParagraphs[0]),
        paragraphs: narrativeParagraphs,
        byline: text(narrativeRow.byline || narrativeRow.author) || undefined,
      } : undefined,
      sources: evidenceRows(todayView.sources, "today-source").length ? evidenceRows(todayView.sources, "today-source") : storySources,
      materiality: Object.keys(rawMateriality).length ? {
        isMaterial: Boolean(rawMateriality.is_material ?? materialityLevel !== "none"),
        level: materialityLevel,
        reason: text(rawMateriality.reason || rawMateriality.summary, "No material decision change was reported."),
        changedAt: text(rawMateriality.changed_at || rawMateriality.as_of) || undefined,
      } : undefined,
      stories: normalizedStories,
    },
    myTeam: {
      summary: `${roster.length} rostered players, with ${list(teamView.cut_line).length} names at the review line. No move is executed by this dashboard.`,
      roster,
      coverage: list<Raw>(teamView.coverage).map((row) => ({ position: text(row.position), actual: number(row.current), target: typeof row.target === "number" ? row.target : null, change: typeof row.gap === "number" ? row.gap : null, note: text(row.target_status, typeof row.gap === "number" ? `${row.gap > 0 ? "+" : ""}${row.gap} versus target` : "Target unavailable") })),
      cutLine: acceptedCutLineValues.length ? Math.max(...acceptedCutLineValues) : 50,
    },
    matchup: {
      overallWinProbability: overall,
      advancementProbability: optionalPercent(matchupView.summary?.advancement_probability),
      modelAssumptions: list<string>(matchupView.summary?.outcome?.assumptions),
      simulations: typeof matchupView.summary?.simulations === "number" ? matchupView.summary.simulations : null,
      currentScore: text(matchupView.summary?.current_score) || undefined,
      innings: text(matchupView.summary?.innings_progress) || undefined,
      projectedScore: `${number(matchupView.summary?.expected_categories).toFixed(1)} expected`,
      recommendation: list<Raw>(todayView.brief).find((row) => text(row.label).includes("call"))?.message ?? "Use category probabilities and roster-valid scenarios together.",
      categories: rawCategories.map((row) => {
        const odds = optionalPercent(row.win_probability);
        return { category: text(row.category), mine: typeof row.mine === "number" ? row.mine : null, theirs: typeof row.opponent === "number" ? row.opponent : null, lowerIsBetter: Boolean(row.lower_is_better), unit: text(row.category), winProbability: odds, swing: odds === null ? 0 : Math.abs(odds - 50), status: odds !== null && odds >= 57 ? "lead" as const : odds !== null && odds <= 43 ? "trail" as const : "toss-up" as const, action: `${text(row.strategy, "Watch")} · ${row.confidence == null ? "confidence unavailable" : `modeled confidence ${percent(row.confidence)}%`}.` };
      }),
      scenarios,
    },
    league: {
      playoffContext: leagueView.playoff_context,
      playoffLine: 6,
      summary: `${text(myStanding.team_name, "Your team")} is ranked ${number(myStanding.rank)} with a ${percent(myStanding.playoff_probability)}% modeled playoff chance.`,
      standings: rawStandings.map((row) => ({ rank: number(row.rank), team: text(row.team_name), wins: number(row.wins), losses: number(row.losses), ties: number(row.ties), winPct: number(row.winning_percentage), gamesBack: number(row.games_back), playoffProbability: percent(row.playoff_probability), power: powerByTeam.get(text(row.team_name)) ?? 50, pickupsLeft: typeof row.pickups_remaining === "number" ? row.pickups_remaining : null, pickupsBasis: text(row.pickups_basis || row.pickups_status) || undefined, pickupsConfidence: row.pickups_confidence === undefined ? undefined : pickupConfidence(row.pickups_confidence), isMine: Boolean(row.is_my_team), isRival: Boolean(row.is_featured_rival) })),
    },
    hitters: { summary: `${hitterRows.length} available hitters, ranked by sustainable value, weekly impact and roster fit.`, players: hitterRows },
    pitchers: { summary: `${pitcherRows.length} available pitchers, with start probabilities and a plain-language next-three-start outlook where evidence exists.`, players: pitcherRows },
    transactions: {
      summary: `${transactionRows.length} recent league moves. Manager motives are labeled as inference unless evidence says otherwise.`,
      rows: transactionRows.map((row, index) => ({ id: `transaction-${index}`, happenedAt: text(row.time), fantasyTeam: text(row.team_name), player: text(row.name), mlbTeam: text(row.mlb_team), type: text(row.action) === "drop" ? "drop" as const : text(row.action) === "trade" ? "trade" as const : "add" as const, worthwhile: worthwhileIds.has(text(row.player_key)), value: number(row.overall_score), reason: text(row.reason), reasonKind: text(row.reason_label).toLowerCase().includes("inference") ? "league inference" as const : "reported" as const, fitForMe: text(row.roster_fit), waiverTiming: text(row.waiver_date || row.ownership, "Available"), confidence: confidence(row.confidence) })),
    },
    method: {
      summary: text(metadata.decision_disclaimer, "Read-only decision support built from the latest accepted snapshot."),
      guardrails: ["Yahoo access is read-only.", "Only an accepted, privacy-gated snapshot is displayed.", "The dashboard never executes a roster action.", "Manager motives are identified as inference unless supported by evidence."],
      sourceNotes: methodSources.map((source) => `${text(source.name)} · ${text(source.as_of, asOf)}`),
      calibration: calibrationSummary(raw.calibration ?? metadata.calibration ?? raw.model_calibration),
    },
  };
}

export async function getDashboardSnapshot(signal?: AbortSignal): Promise<{ data: DashboardSnapshotV2; isFixture: boolean }> {
  if (import.meta.env.VITE_FIXTURE === "1") return { data: fallbackSnapshot, isFixture: true };
  try {
    const response = await fetch("/api/v2/dashboard", {
      cache: "no-store",
      headers: { Accept: "application/json" },
      signal,
    });
    if (!response.ok) throw new Error(`Dashboard API returned ${response.status}`);
    const payload: unknown = await response.json();
    if (isSnapshot(payload)) return { data: payload, isFixture: false };
    if (payload && typeof payload === "object" && (payload as Raw).record_type === "dashboard_snapshot" && number((payload as Raw).schema_version) === 2) {
      return { data: normalizeRawSnapshot(payload as Raw), isFixture: false };
    }
    throw new Error("Dashboard API returned an unsupported snapshot");
  } catch (error) {
    if (signal?.aborted) throw error;
    if (import.meta.env.DEV || import.meta.env.MODE === "test") {
      return { data: fallbackSnapshot, isFixture: true };
    }
    throw error;
  }
}
